import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  Instagram, 
  MessageCircle, 
  Phone, 
  Mail, 
  Palette, 
  Ruler, 
  Armchair,
  Sofa,
  Lamp,
  BedDouble,
  LayoutGrid,
  ChevronDown,
  CheckCircle,
  Loader2,
  ExternalLink,
  ImagePlus,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Service packages
const services = [
  {
    id: 'asesoria-virtual',
    name: 'Asesoría Virtual',
    price: 48.95,
    description: 'Consulta virtual personalizada para 1 área a decorar',
    features: ['Consulta para 1 área a decorar', 'Recomendaciones', 'Respuestas a tus dudas', 'Moodboard', 'Cotización de productos sugeridos', 'Atención personalizada'],
    icon: MessageCircle,
    popular: true
  },
  {
    id: 'asesoria-premium',
    name: 'Asesoría Premium',
    price: 128.95,
    description: 'Consulta para hasta 3 áreas a decorar',
    features: ['Consulta para hasta 3 áreas a decorar', 'Moodboard', 'Definición de estilo', 'Cotización de muebles sugeridos', 'Atención personalizada'],
    icon: Palette,
    popular: false
  },
  {
    id: 'proyecto-completo',
    name: 'Proyecto Completo',
    price: null,
    customQuote: true,
    description: 'A cotizar según el alcance de tu proyecto',
    features: ['Todo lo de las asesorías', 'Layout profesional', 'Visita al espacio', 'Escogencia de acabados', 'Diseño de ambientes', 'Restructuración completa del espacio', 'Compras de muebles e instalación', 'Gestión integral del proyecto y mucho más', 'Seguimiento personalizado hasta el final'],
    icon: LayoutGrid,
    popular: false
  }
];

const furnitureCategories = {
  sala: {
    name: 'Sala',
    icon: Sofa,
    subcategories: ['Sofás', 'Seccionales', 'Mesas de centro', 'Mesas laterales', 'Consolas', 'Muebles de TV', 'Estantería', 'Butacas', 'Reposa pies']
  },
  comedor: {
    name: 'Comedor',
    icon: LayoutGrid,
    subcategories: ['Mesas de comedor', 'Sillas', 'Bancas', 'Buffets', 'Vitrinas', 'Bares']
  },
  recamara: {
    name: 'Recámara',
    icon: BedDouble,
    subcategories: ['Camas', 'Mesitas de noche', 'Gaveteros', 'Muebles de TV', 'Closets', 'Sillas de lectura', 'Tocadores']
  },
  alfombra: {
    name: 'Alfombras',
    icon: LayoutGrid,
    subcategories: ['Alfombras de sala', 'Alfombras de dormitorio', 'Alfombras de pasillo', 'Tapetes']
  },
  iluminacion: {
    name: 'Iluminación',
    icon: Lamp,
    subcategories: ['Lámparas de piso', 'Lámparas de mesa', 'Lámparas colgantes', 'Apliques de pared', 'Candelabros']
  },
  organizacion: {
    name: 'Organización',
    icon: LayoutGrid,
    subcategories: ['Estantes', 'Cajas decorativas', 'Cestas', 'Organizadores de closet', 'Percheros']
  },
  decoracion: {
    name: 'Decoración',
    icon: Armchair,
    subcategories: ['Cojines', 'Mantas', 'Cortinas', 'Jarrones', 'Velas', 'Plantas artificiales', 'Espejos']
  },
  arte: {
    name: 'Arte',
    icon: LayoutGrid,
    subcategories: ['Cuadros', 'Fotografías', 'Esculturas', 'Arte de pared', 'Murales']
  }
};

const styles = [
  { id: 'moderno', name: 'Moderno' },
  { id: 'contemporaneo', name: 'Contemporáneo' },
  { id: 'clasico', name: 'Clásico' },
  { id: 'vintage', name: 'Vintage' },
  { id: 'elegante', name: 'Elegante' },
  { id: 'minimalista', name: 'Minimalista' },
  { id: 'minimalista-calido', name: 'Minimalista Cálido' },
  { id: 'japandi', name: 'Japandi' },
  { id: 'bohemio', name: 'Bohemio' },
  { id: 'industrial', name: 'Industrial' },
  { id: 'escandinavo', name: 'Escandinavo' }
];

export default function LandingPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  
  // Portfolio and Tips state
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [tips, setTips] = useState([]);
  const [selectedPortfolioItem, setSelectedPortfolioItem] = useState(null);
  
  // Contact form state
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    service_interest: ''
  });
  
  // Furniture order form state
  const [furnitureForm, setFurnitureForm] = useState({
    name: '',
    email: '',
    phone: '',
    furniture_type: '',
    furniture_subcategory: '',
    style: '',
    budget_min: '',
    budget_max: '',
    additional_notes: '',
    space_image: ''
  });
  
  // Selected category for showing subcategories
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  // Payment form state
  const [paymentForm, setPaymentForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: ''
  });

  const fetchPortfolio = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/portfolio`);
      const data = await res.json();
      setPortfolioItems(data.items || []);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error fetching portfolio:', error);
    }
  }, []);

  const fetchTips = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/tips`);
      const data = await res.json();
      setTips(data.tips || []);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error fetching tips:', error);
    }
  }, []);

  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 5;

    if (attempts >= maxAttempts) {
      setPaymentStatus('timeout');
      toast.error('No pudimos verificar el pago. Por favor contáctanos.');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/checkout/status/${sessionId}`);
      const data = await response.json();

      if (data.payment_status === 'paid') {
        setPaymentStatus('success');
        toast.success('¡Pago exitoso! Te contactaremos pronto para agendar tu cita.');
        return;
      } else if (data.status === 'expired') {
        setPaymentStatus('expired');
        toast.error('La sesión de pago expiró. Por favor intenta nuevamente.');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error checking payment:', error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    }
  }, []);

  // Fetch portfolio and tips on mount
  useEffect(() => {
    fetchPortfolio();
    fetchTips();
  }, [fetchPortfolio, fetchTips]);

  // Check for payment return
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentResult = urlParams.get('payment');
    const sessionId = urlParams.get('session_id');

    if (paymentResult === 'success' && sessionId) {
      pollPaymentStatus(sessionId);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (paymentResult === 'cancelled') {
      toast.error('Pago cancelado. Puedes intentarlo nuevamente.');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [pollPaymentStatus]);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactForm)
      });
      
      const data = await response.json();
      
      if (data.ok) {
        toast.success(data.message);
        setContactForm({ name: '', email: '', phone: '', message: '', service_interest: '' });
      } else {
        toast.error(data.detail || 'Error al enviar el formulario');
      }
    } catch (error) {
      toast.error('Error de conexión. Inténtalo de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSpaceImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor sube un archivo de imagen.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        // Comprimir/redimensionar para mantener el envío liviano
        const maxDim = 1200;
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          if (width >= height) { height = Math.round(height * maxDim / width); width = maxDim; }
          else { width = Math.round(width * maxDim / height); height = maxDim; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        const compressed = canvas.toDataURL('image/jpeg', 0.7);
        setFurnitureForm((prev) => ({ ...prev, space_image: compressed }));
        toast.success('Foto del espacio agregada');
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleFurnitureSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/furniture-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...furnitureForm,
          budget_min: parseFloat(furnitureForm.budget_min),
          budget_max: parseFloat(furnitureForm.budget_max)
        })
      });
      
      const data = await response.json();
      
      if (data.ok) {
        // Abrir WhatsApp con los datos del cliente para atención directa
        const categoryName = furnitureCategories[furnitureForm.furniture_type]?.name || furnitureForm.furniture_type;
        const waMessage =
          `*Nueva solicitud de muebles - Decorando Vamos*\n\n` +
          `*Nombre:* ${furnitureForm.name}\n` +
          `*Email:* ${furnitureForm.email}\n` +
          `*Telefono:* ${furnitureForm.phone}\n` +
          `*Categoria:* ${categoryName}\n` +
          (furnitureForm.furniture_subcategory ? `*Subcategoria:* ${furnitureForm.furniture_subcategory}\n` : '') +
          (furnitureForm.style ? `*Estilo:* ${furnitureForm.style}\n` : '') +
          `*Presupuesto:* $${furnitureForm.budget_min} - $${furnitureForm.budget_max}\n` +
          (furnitureForm.additional_notes ? `*Notas:* ${furnitureForm.additional_notes}\n` : '') +
          (furnitureForm.space_image ? `*Foto del espacio:* adjuntada en el sitio (revísala en el panel)\n` : '');
        const waUrl = `https://wa.me/50767801300?text=${encodeURIComponent(waMessage)}`;
        window.open(waUrl, '_blank', 'noopener,noreferrer');

        toast.success(data.message);
        setFurnitureForm({
          name: '', email: '', phone: '', furniture_type: '',
          furniture_subcategory: '', style: '', budget_min: '', budget_max: '', additional_notes: '', space_image: ''
        });
        setSelectedCategory(null);
      } else {
        toast.error(data.detail || 'Error al enviar el pedido');
      }
    } catch (error) {
      toast.error('Error de conexión. Inténtalo de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    if (!selectedService) return;
    
    setIsLoading(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          package_id: selectedService.id,
          customer_name: paymentForm.customer_name,
          customer_email: paymentForm.customer_email,
          customer_phone: paymentForm.customer_phone,
          origin_url: window.location.origin
        })
      });
      
      const data = await response.json();
      
      if (data.ok && data.url) {
        window.location.href = data.url;
      } else {
        toast.error(data.detail || 'Error al procesar el pago');
      }
    } catch (error) {
      toast.error('Error de conexión. Inténtalo de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  const openPaymentModal = (service) => {
    setSelectedService(service);
    setShowPaymentModal(true);
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-beige-100">
      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/50767801300?text=Hola%20Pamela,%20me%20interesa%20una%20asesor%C3%ADa%20de%20decoraci%C3%B3n"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-[#25D366] text-white p-4 rounded-full shadow-2xl hover:scale-110 transition-transform duration-300 whatsapp-pulse"
        data-testid="whatsapp-float-btn"
      >
        <MessageCircle size={28} fill="white" />
      </a>

      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center justify-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1615875605825-5eb9bb5d52ac?auto=format&fit=crop&w=1920&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/25 to-black/40" />
        <div className="relative z-10 text-center text-white px-6 max-w-4xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-8"
            data-testid="hero-logo"
          >
            <div className="w-64 md:w-80 mx-auto p-4 bg-white/70 backdrop-blur-sm rounded-2xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_brave-cohen-2/artifacts/y5uu8k52_Decorando%20Vamos%20LOGO%20NEW.png"
                alt="Decorando Vamos con Pamela E."
                className="w-full drop-shadow-lg"
              />
            </div>
          </motion.div>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl md:text-2xl mb-10 font-light opacity-95 drop-shadow-lg"
          >
            Transforma tus espacios en hogares llenos de estilo y personalidad
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <button
              onClick={() => scrollToSection('servicios')}
              className="bg-olive-500 hover:bg-olive-600 text-white px-10 py-4 rounded-full text-lg font-medium transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1"
              data-testid="cta-services-btn"
            >
              Ver Servicios
            </button>
            <a
              href="https://wa.me/50767801300?text=Hola%20Pamela,%20me%20interesa%20una%20asesor%C3%ADa"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-transparent border-2 border-white text-white px-10 py-4 rounded-full text-lg font-medium hover:bg-white hover:text-olive-500 transition-all duration-300"
              data-testid="cta-whatsapp-btn"
            >
              <MessageCircle className="inline mr-2" size={20} />
              WhatsApp
            </a>
          </motion.div>
        </div>
        <button 
          onClick={() => scrollToSection('sobre-mi')}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 text-white animate-bounce"
          aria-label="Scroll down"
        >
          <ChevronDown size={40} />
        </button>
      </section>

      {/* About Section */}
      <section id="sobre-mi" className="section-padding px-6 md:px-12 lg:px-24 relative overflow-hidden" data-testid="about-section">
        {/* Decorative leaf background to match the logo */}
        <div className="pointer-events-none absolute inset-0 z-0" aria-hidden="true">
          <svg className="absolute -top-10 -left-12 w-64 h-64 md:w-80 md:h-80 text-olive-300 opacity-[0.18] -rotate-12" viewBox="0 0 100 100" fill="currentColor">
            <path d="M50 5 C30 20, 10 50, 50 95 C90 50, 70 20, 50 5" />
            <path d="M50 12 L50 90" stroke="white" strokeWidth="1.2" fill="none" opacity="0.5" />
          </svg>
          <svg className="absolute top-1/3 -right-16 w-72 h-72 md:w-96 md:h-96 text-olive-200 opacity-[0.22] rotate-[160deg]" viewBox="0 0 100 100" fill="currentColor">
            <path d="M50 5 C30 20, 10 50, 50 95 C90 50, 70 20, 50 5" />
            <path d="M50 12 L50 90" stroke="white" strokeWidth="1.2" fill="none" opacity="0.5" />
          </svg>
          <svg className="absolute -bottom-12 left-1/4 w-56 h-56 md:w-72 md:h-72 text-olive-300 opacity-[0.14] rotate-[35deg]" viewBox="0 0 100 100" fill="currentColor">
            <path d="M50 5 C30 20, 10 50, 50 95 C90 50, 70 20, 50 5" />
            <path d="M50 12 L50 90" stroke="white" strokeWidth="1.2" fill="none" opacity="0.5" />
          </svg>
        </div>
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center relative z-10">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="relative w-80 md:w-96 mx-auto">
              {/* Decorative border */}
              <div className="absolute inset-0 rounded-2xl border-4 border-olive-200 -rotate-3"></div>
              <div className="absolute inset-0 rounded-2xl border-4 border-olive-300 rotate-2"></div>
              {/* Main image */}
              <img
                src="https://customer-assets.emergentagent.com/job_brave-cohen-2/artifacts/eh17d8fj_WhatsApp%20Image%202026-02-15%20at%201.39.30%20PM.jpeg"
                alt="Pamela E. - Diseñadora de Interiores"
                className="w-full h-auto rounded-2xl shadow-floating relative z-10 border-4 border-white"
              />
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-olive-500 font-medium uppercase tracking-wider text-sm">Sobre Mí</span>
            <h2 className="font-heading text-4xl md:text-5xl font-medium mt-2 mb-6 text-foreground">
              Bienvenido a Decorando Vamos
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-4">
              Soy Pamela, Interiorista y Administradora de Empresas con 7 años de experiencia transformando espacios en experiencias que se viven, se sienten y se aman.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Creo profundamente que cada espacio debe ser un lugar al que perteneces — uno que te calma, te inspira y te refleja. El hogar es mucho más que un entorno físico: es el reflejo de todo lo hermoso que anhelamos, nuestra fuente diaria de equilibrio y bienestar.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Un espacio bien pensado transforma la manera en que habitamos, y eso influye directamente en nuestro estado de ánimo y nuestra energía cotidiana. Por eso, mi enfoque va más allá de la estética: combino creatividad, planificación estratégica y una gestión inteligente de los recursos para lograr proyectos armónicos, funcionales y coherentes — adaptados a cada cliente y a cada presupuesto.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-6">
              Y si no cuentas con una gran inversión, también estoy para ti. A veces, los cambios más pequeños — una nueva iluminación, piezas clave, textiles o una mejor distribución — pueden transformar por completo un espacio y darle el upgrade que necesita.
            </p>

            <h3 className="font-heading text-2xl md:text-3xl font-medium mb-3 text-foreground">¿Cómo trabajamos?</h3>
            <p className="text-muted-foreground leading-relaxed mb-5">
              Hacemos de tu proceso de decoración algo placentero, organizado y completamente a tu medida. Todo puede gestionarse de manera virtual, desde la comodidad de tu hogar — sin que tengas que mover un dedo.
            </p>
            <ul className="space-y-3 mb-6" data-testid="metodo-trabajo-list">
              {[
                { t: 'Te escuchamos', d: 'Comenzamos por entender tu visión, tu estilo, tu espacio y tus necesidades.' },
                { t: 'Te asesoramos', d: 'Orientamos cada decisión con criterio profesional para que aproveches al máximo cada recurso.' },
                { t: 'Creamos tu moodboard', d: 'Para que visualices el resultado antes de invertir.' },
                { t: 'Proponemos el mobiliario ideal', d: 'Seleccionamos piezas de calidad que se ajustan a tu presupuesto y personalidad.' },
                { t: 'Armamos tu cotización', d: 'Clara, organizada y sin sorpresas.' },
                { t: 'Llevamos e instalamos todo en tu espacio', d: 'Nos encargamos de principio a fin.' },
              ].map((step, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-olive-500 text-white flex items-center justify-center text-sm font-medium mt-0.5">{i + 1}</span>
                  <p className="text-muted-foreground leading-relaxed">
                    <span className="font-medium text-foreground">{step.t}</span> — {step.d}
                  </p>
                </li>
              ))}
            </ul>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Conmigo no solo encontrarás asesoría en diseño y decoración, sino una aliada cercana que te acompañará en cada decisión y te proveerá los artículos que tu hogar necesita para cobrar nueva vida.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Más que diseñar interiores, mi propósito es generar armonía y bienestar en la vida de cada cliente al que tengo el privilegio de acompañar.
            </p>
            <p className="text-olive-500 font-medium text-lg mb-6">
              ✨ Agenda una cita y conversemos. Tu espacio puede comenzar a transformarse hoy.
            </p>
            <a
              href="https://instagram.com/decorandovamos"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-olive-500 hover:text-olive-600 font-medium text-lg transition-colors"
              data-testid="instagram-link"
            >
              <Instagram size={24} />
              @decorandovamos
            </a>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section id="servicios" className="section-padding px-6 md:px-12 lg:px-24 bg-white sketch-bg" data-testid="services-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-olive-500 font-medium uppercase tracking-wider text-sm">Servicios</span>
            <h2 className="font-heading text-4xl md:text-5xl font-medium mt-2 mb-4">Nuestros Servicios</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Elige el servicio que mejor se adapte a tus necesidades y transforma tu espacio
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`service-card bg-beige-200 p-8 rounded-xl border border-beige-300 hover:border-olive-300 relative overflow-hidden ${
                  service.popular ? 'ring-2 ring-olive-500' : ''
                }`}
                data-testid={`service-card-${service.id}`}
              >
                {service.popular && (
                  <span className="absolute top-4 right-4 bg-olive-500 text-white text-xs px-3 py-1 rounded-full font-medium">
                    Más Popular
                  </span>
                )}
                <service.icon className="text-olive-500 mb-4" size={40} />
                <h3 className="font-heading text-2xl font-medium mb-2">{service.name}</h3>
                <p className="text-muted-foreground mb-4">{service.description}</p>
                <p className="text-3xl font-bold text-olive-500 mb-6">
                  {service.customQuote ? 'A cotizar' : `$${service.price}`}
                </p>
                <ul className="space-y-2 mb-6">
                  {service.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <CheckCircle className="text-olive-500 flex-shrink-0" size={16} />
                      {feature}
                    </li>
                  ))}
                </ul>
                <a
                  href={service.customQuote 
                    ? `https://wa.me/50767801300?text=Hola%20Pamela,%20me%20interesa%20el%20servicio%20${encodeURIComponent(service.name)}%20y%20me%20gustaría%20recibir%20una%20cotización`
                    : `https://wa.me/50767801300?text=Hola%20Pamela,%20me%20interesa%20el%20servicio%20${encodeURIComponent(service.name)}%20($${service.price})`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full bg-olive-500 hover:bg-olive-600 text-white py-3 rounded-full font-medium transition-all duration-300 block text-center"
                  data-testid={`buy-btn-${service.id}`}
                >
                  {service.customQuote ? 'Solicitar Cotización' : 'Contratar'}
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      {portfolioItems.length > 0 && (
        <section id="proyectos" className="section-padding px-6 md:px-12 lg:px-24 bg-beige-100" data-testid="portfolio-section">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <span className="text-olive-500 font-medium uppercase tracking-wider text-sm">Portafolio</span>
              <h2 className="font-heading text-4xl md:text-5xl font-medium mt-2 mb-4">Nuestros Proyectos</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Espacios que hemos ayudado a equipar y embellecer
              </p>
              <p className="text-sm text-muted-foreground/80 max-w-2xl mx-auto mt-3 italic">
                Nota: En algunos de estos proyectos nuestra participación fue a través del mobiliario y la iluminación. Varias imágenes pertenecen a clientes que confiaron en nuestros productos para dar vida a sus espacios.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {portfolioItems.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white rounded-xl overflow-hidden shadow-card hover:shadow-lg transition-shadow cursor-pointer group"
                  onClick={() => setSelectedPortfolioItem(item)}
                  data-testid={`portfolio-item-${item.id}`}
                >
                  <div className="relative h-64 overflow-hidden">
                    <img 
                      src={item.image_after} 
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    {item.image_before && (
                      <span className="absolute top-3 left-3 bg-olive-500 text-white text-xs px-3 py-1 rounded-full">
                        Antes / Después
                      </span>
                    )}
                    <span className="absolute bottom-3 right-3 bg-white/90 text-foreground text-xs px-3 py-1 rounded-full capitalize">
                      {item.category}
                    </span>
                  </div>
                  <div className="p-5">
                    <h3 className="font-heading text-xl font-medium mb-2">{item.title}</h3>
                    {item.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2">{item.description}</p>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Tips Section */}
      {tips.length > 0 && (
        <section id="tips" className="section-padding px-6 md:px-12 lg:px-24 bg-white" data-testid="tips-section">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <span className="text-olive-500 font-medium uppercase tracking-wider text-sm">Blog</span>
              <h2 className="font-heading text-4xl md:text-5xl font-medium mt-2 mb-4">Tips de Decoración</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Consejos para transformar tu hogar
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {tips.map((tip, index) => (
                <motion.article
                  key={tip.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-beige-100 rounded-xl overflow-hidden"
                  data-testid={`tip-${tip.id}`}
                >
                  {tip.image_url && (
                    <img 
                      src={tip.image_url} 
                      alt={tip.title}
                      className="w-full h-48 object-cover"
                    />
                  )}
                  {tip.video_url && !tip.image_url && (
                    <div className="w-full h-48 bg-olive-100 flex items-center justify-center">
                      <span className="text-olive-500 text-4xl">🎬</span>
                    </div>
                  )}
                  <div className="p-6">
                    <span className="text-olive-500 text-xs font-medium uppercase tracking-wider">{tip.category}</span>
                    <h3 className="font-heading text-xl font-medium mt-1 mb-3">{tip.title}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-3">{tip.content}</p>
                    {tip.video_url && (
                      <a 
                        href={tip.video_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-olive-500 hover:text-olive-600 text-sm font-medium mt-4"
                      >
                        Ver video <ExternalLink size={14} />
                      </a>
                    )}
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Furniture Order Section */}
      <section id="muebles" className="section-padding px-6 md:px-12 lg:px-24 bg-beige-200" data-testid="furniture-section">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-olive-500 font-medium uppercase tracking-wider text-sm">Pedido de Muebles</span>
            <h2 className="font-heading text-4xl md:text-5xl font-medium mt-2 mb-4">Encuentra el Mueble Perfecto</h2>
            <p className="text-lg text-muted-foreground">
              Cuéntanos qué buscas y te presentaremos opciones personalizadas
            </p>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            onSubmit={handleFurnitureSubmit}
            className="bg-white p-8 md:p-12 rounded-2xl shadow-card space-y-6"
            data-testid="furniture-form"
          >
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-2">Nombre *</label>
                <input
                  type="text"
                  required
                  value={furnitureForm.name}
                  onChange={(e) => setFurnitureForm({...furnitureForm, name: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg border border-beige-300 focus:border-olive-500 transition-colors"
                  placeholder="Tu nombre"
                  data-testid="furniture-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Email *</label>
                <input
                  type="email"
                  required
                  value={furnitureForm.email}
                  onChange={(e) => setFurnitureForm({...furnitureForm, email: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg border border-beige-300 focus:border-olive-500 transition-colors"
                  placeholder="tu@email.com"
                  data-testid="furniture-email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Teléfono / WhatsApp *</label>
              <input
                type="tel"
                required
                value={furnitureForm.phone}
                onChange={(e) => setFurnitureForm({...furnitureForm, phone: e.target.value})}
                className="w-full px-4 py-3 rounded-lg border border-beige-300 focus:border-olive-500 transition-colors"
                placeholder="+507 6780-1300"
                data-testid="furniture-phone-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-3">Categoría *</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(furnitureCategories).map(([key, category]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => {
                      setSelectedCategory(key);
                      setFurnitureForm({...furnitureForm, furniture_type: key, furniture_subcategory: ''});
                    }}
                    className={`p-4 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${
                      furnitureForm.furniture_type === key
                        ? 'border-olive-500 bg-olive-50'
                        : 'border-beige-300 hover:border-olive-300'
                    }`}
                    data-testid={`furniture-type-${key}`}
                  >
                    <category.icon size={24} className={furnitureForm.furniture_type === key ? 'text-olive-500' : 'text-muted-foreground'} />
                    <span className="text-sm font-medium">{category.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {selectedCategory && (
              <div>
                <label className="block text-sm font-medium mb-3">¿Qué buscas en {furnitureCategories[selectedCategory].name}? *</label>
                <div className="flex flex-wrap gap-2">
                  {furnitureCategories[selectedCategory].subcategories.map((sub) => (
                    <button
                      key={sub}
                      type="button"
                      onClick={() => setFurnitureForm({...furnitureForm, furniture_subcategory: sub})}
                      className={`px-4 py-2 rounded-full border-2 transition-all text-sm font-medium ${
                        furnitureForm.furniture_subcategory === sub
                          ? 'border-olive-500 bg-olive-500 text-white'
                          : 'border-beige-300 hover:border-olive-300'
                      }`}
                      data-testid={`furniture-sub-${sub}`}
                    >
                      {sub}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-3">Estilo Preferido *</label>
              <div className="flex flex-wrap gap-2">
                {styles.map((style) => (
                  <button
                    key={style.id}
                    type="button"
                    onClick={() => setFurnitureForm({...furnitureForm, style: style.id})}
                    className={`px-4 py-2 rounded-full border-2 transition-all text-sm font-medium ${
                      furnitureForm.style === style.id
                        ? 'border-olive-500 bg-olive-500 text-white'
                        : 'border-beige-300 hover:border-olive-300'
                    }`}
                    data-testid={`furniture-style-${style.id}`}
                  >
                    {style.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-2">Presupuesto Mínimo ($) *</label>
                <input
                  type="number"
                  required
                  min="0"
                  value={furnitureForm.budget_min}
                  onChange={(e) => setFurnitureForm({...furnitureForm, budget_min: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg border border-beige-300 focus:border-olive-500 transition-colors"
                  placeholder="500"
                  data-testid="furniture-budget-min"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Presupuesto Máximo ($) *</label>
                <input
                  type="number"
                  required
                  min="0"
                  value={furnitureForm.budget_max}
                  onChange={(e) => setFurnitureForm({...furnitureForm, budget_max: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg border border-beige-300 focus:border-olive-500 transition-colors"
                  placeholder="2000"
                  data-testid="furniture-budget-max"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Notas Adicionales</label>
              <textarea
                value={furnitureForm.additional_notes}
                onChange={(e) => setFurnitureForm({...furnitureForm, additional_notes: e.target.value})}
                className="w-full px-4 py-3 rounded-lg border border-beige-300 focus:border-olive-500 transition-colors h-32 resize-none"
                placeholder="Cuéntanos más sobre lo que buscas, dimensiones, colores preferidos..."
                data-testid="furniture-notes"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Foto de tu espacio (opcional)</label>
              <p className="text-sm text-muted-foreground mb-3">Sube una foto del lugar donde quieres el mueble para darte mejores recomendaciones.</p>
              {!furnitureForm.space_image ? (
                <label
                  htmlFor="space-image-input"
                  className="flex flex-col items-center justify-center gap-2 w-full py-8 rounded-lg border-2 border-dashed border-beige-300 hover:border-olive-500 cursor-pointer transition-colors text-muted-foreground hover:text-olive-500"
                  data-testid="furniture-image-dropzone"
                >
                  <ImagePlus size={32} />
                  <span className="text-sm font-medium">Toca para subir una foto</span>
                  <span className="text-xs">JPG o PNG</span>
                  <input
                    id="space-image-input"
                    type="file"
                    accept="image/*"
                    onChange={handleSpaceImageUpload}
                    className="hidden"
                    data-testid="furniture-image-input"
                  />
                </label>
              ) : (
                <div className="relative w-full rounded-lg overflow-hidden border border-beige-300" data-testid="furniture-image-preview">
                  <img src={furnitureForm.space_image} alt="Espacio del cliente" className="w-full max-h-64 object-cover" />
                  <button
                    type="button"
                    onClick={() => setFurnitureForm({...furnitureForm, space_image: ''})}
                    className="absolute top-2 right-2 bg-white/90 hover:bg-white text-foreground rounded-full p-2 shadow-md"
                    data-testid="furniture-image-remove"
                    aria-label="Quitar foto"
                  >
                    <X size={18} />
                  </button>
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading || !furnitureForm.furniture_type || !furnitureForm.furniture_subcategory || !furnitureForm.style}
              className="w-full bg-olive-500 hover:bg-olive-600 disabled:bg-olive-300 text-white py-4 rounded-full font-medium text-lg transition-all duration-300 flex items-center justify-center gap-2"
              data-testid="furniture-submit-btn"
            >
              {isLoading ? (
                <><Loader2 className="animate-spin" size={20} /> Enviando...</>
              ) : (
                'Solicitar Opciones de Muebles'
              )}
            </button>
          </motion.form>

          {/* Product Carousel */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12"
          >
            <p className="text-center text-muted-foreground mb-6">✨ Algunos de nuestros productos destacados</p>
            <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {[
                { img: "https://customer-assets.emergentagent.com/job_9e3b270a-eae8-4d27-a904-bd4f483f6bf2/artifacts/oa7acxji_WhatsApp%20Image%202026-06-17%20at%202.57.05%20PM%20%281%29.jpeg", name: "Sofá tipo Cloud" },
                { img: "https://customer-assets.emergentagent.com/job_9e3b270a-eae8-4d27-a904-bd4f483f6bf2/artifacts/hn3za12y_WhatsApp%20Image%202026-06-17%20at%202.56.28%20PM%20%281%29.jpeg", name: "Silla de Acento Verde" },
                { img: "https://customer-assets.emergentagent.com/job_9e3b270a-eae8-4d27-a904-bd4f483f6bf2/artifacts/pofnar7r_WhatsApp%20Image%202026-06-17%20at%202.56.38%20PM%20%281%29.jpeg", name: "Mueble de TV" },
                { img: "https://customer-assets.emergentagent.com/job_9e3b270a-eae8-4d27-a904-bd4f483f6bf2/artifacts/fvyvlehl_WhatsApp%20Image%202026-06-17%20at%202.56.46%20PM%20%281%29.jpeg", name: "Mesa de Centro Redonda" }
              ].map((product, index) => (
                <a
                  key={product.name}
                  href="https://instagram.com/decorandovamos"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-shrink-0 snap-center group"
                >
                  <div className="w-48 h-48 md:w-56 md:h-56 rounded-xl overflow-hidden shadow-card group-hover:shadow-lg transition-shadow">
                    <img 
                      src={product.img} 
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <p className="text-center mt-2 text-sm font-medium text-foreground">{product.name}</p>
                </a>
              ))}
            </div>
            <p className="text-center text-sm text-olive-500 mt-4">
              <a href="https://instagram.com/decorandovamos" target="_blank" rel="noopener noreferrer" className="hover:underline">
                Ver más en @decorandovamos →
              </a>
            </p>
          </motion.div>
        </div>
      </section>

      {/* Amazon Affiliate Section */}
      <section className="section-padding px-6 md:px-12 lg:px-24 bg-white decorated-section" data-testid="amazon-section">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-olive-500 font-medium uppercase tracking-wider text-sm">Recomendados</span>
            <h2 className="font-heading text-4xl md:text-5xl font-medium mt-2 mb-4">Productos que Amamos</h2>
            <p className="text-lg text-muted-foreground">
              Nuestra selección de favoritos disponibles en Amazon
            </p>
          </motion.div>

          <div className="grid grid-cols-3 md:grid-cols-3 lg:grid-cols-9 gap-4">
            {[
              { name: "Recámara", emoji: "🛏️" },
              { name: "Sala", emoji: "🛋️" },
              { name: "Comedor", emoji: "🍽️" },
              { name: "Cocina", emoji: "🍳" },
              { name: "Organización", emoji: "📦" },
              { name: "Iluminación", emoji: "💡" },
              { name: "Entretenimiento", emoji: "📺" },
              { name: "Niños", emoji: "🧸" },
              { name: "Belleza", emoji: "✨" }
            ].map((item, index) => (
              <motion.a
                key={item.name}
                href="https://amazon.com"
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-beige-100 p-5 rounded-xl text-center hover:shadow-lg transition-all group hover:-translate-y-1"
              >
                <div className="text-3xl mb-2">{item.emoji}</div>
                <h3 className="font-medium text-sm text-foreground group-hover:text-olive-500 transition-colors">{item.name}</h3>
                <span className="inline-block mt-2 text-xs text-olive-500 font-medium opacity-0 group-hover:opacity-100 transition-opacity">Ver →</span>
              </motion.a>
            ))}
          </div>

          <div className="mt-10 max-w-2xl mx-auto text-center bg-beige-100 rounded-xl px-6 py-4" data-testid="amazon-disclosure">
            <p className="text-sm text-muted-foreground">
              Como Asociado de Amazon, gano por las compras que califiquen.
            </p>
            <p className="text-xs text-muted-foreground/80 mt-1">
              Decorando Vamos participa en el Programa de Afiliados de Amazon, diseñado para que los sitios web ganen comisiones por publicidad enlazando a Amazon. Los precios y la disponibilidad de los productos pueden variar.
            </p>
          </div>
        </div>
      </section>

      {/* Payment Methods Section */}
      <section className="section-padding px-6 md:px-12 lg:px-24 bg-white decorated-section" data-testid="payment-section">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-olive-500 font-medium uppercase tracking-wider text-sm">Métodos de Pago</span>
            <h2 className="font-heading text-4xl md:text-5xl font-medium mt-2 mb-4">Formas de Pago</h2>
            <p className="text-lg text-muted-foreground">
              Elige el método que más te convenga
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {/* Yappy */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0 }}
              className="bg-[#E8F4FC] p-8 rounded-2xl text-center border-2 border-[#00A3E0]/20"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_brave-cohen-2/artifacts/rqa04l2s_image.png"
                alt="Yappy"
                className="w-32 h-32 mx-auto mb-4 object-contain"
              />
              <p className="text-[#00A3E0] text-sm mb-4 font-medium">Banco General - Panamá</p>
              <p className="text-4xl font-bold text-[#004B87] mb-2">6780-1300</p>
              <div className="mt-4 pt-4 border-t border-[#00A3E0]/20">
                <p className="text-xs text-[#004B87]/60">
                  Envía el comprobante por WhatsApp
                </p>
              </div>
            </motion.div>

            {/* ACH Transfer */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-beige-200 p-8 rounded-2xl text-center border-2 border-beige-300"
            >
              <div className="bg-olive-500 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                </svg>
              </div>
              <h3 className="font-heading text-2xl font-medium mb-1">Transferencia ACH</h3>
              <p className="text-muted-foreground text-sm mb-4">Banco General - Cuenta de Ahorros</p>
              <p className="text-2xl font-bold text-olive-500 mb-2">04-7101-988985-3</p>
              <p className="text-lg font-medium text-foreground">Pamela Espino Vasquez</p>
              <div className="mt-4 pt-4 border-t border-beige-300">
                <p className="text-xs text-muted-foreground">
                  Envía el comprobante por WhatsApp
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contacto" className="section-padding px-6 md:px-12 lg:px-24 bg-beige-100" data-testid="contact-section">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-olive-500 font-medium uppercase tracking-wider text-sm">Contacto</span>
            <h2 className="font-heading text-4xl md:text-5xl font-medium mt-2 mb-4">Hablémos</h2>
            <p className="text-lg text-muted-foreground">
              ¿Tienes preguntas? Estamos aquí para ayudarte
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="flex items-center gap-4">
                <div className="bg-olive-500 p-4 rounded-full">
                  <Phone className="text-white" size={24} />
                </div>
                <div>
                  <p className="font-medium">WhatsApp</p>
                  <a href="https://wa.me/50767801300" className="text-olive-500 hover:underline">+507 6780-1300</a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="bg-olive-500 p-4 rounded-full">
                  <Mail className="text-white" size={24} />
                </div>
                <div>
                  <p className="font-medium">Email</p>
                  <p className="text-muted-foreground">decorandovamos@gmail.com</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="bg-olive-500 p-4 rounded-full">
                  <Instagram className="text-white" size={24} />
                </div>
                <div>
                  <p className="font-medium">Instagram</p>
                  <a 
                    href="https://instagram.com/decorandovamos" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-olive-500 hover:underline"
                  >
                    @decorandovamos
                  </a>
                </div>
              </div>
            </motion.div>

            <motion.form
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              onSubmit={handleContactSubmit}
              className="space-y-4"
              data-testid="contact-form"
            >
              <input
                type="text"
                required
                value={contactForm.name}
                onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                className="w-full px-4 py-3 rounded-lg border border-beige-300 focus:border-olive-500 transition-colors bg-white"
                placeholder="Tu nombre *"
                data-testid="contact-name-input"
              />
              <input
                type="email"
                required
                value={contactForm.email}
                onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                className="w-full px-4 py-3 rounded-lg border border-beige-300 focus:border-olive-500 transition-colors bg-white"
                placeholder="Tu email *"
                data-testid="contact-email-input"
              />
              <input
                type="tel"
                value={contactForm.phone}
                onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                className="w-full px-4 py-3 rounded-lg border border-beige-300 focus:border-olive-500 transition-colors bg-white"
                placeholder="Tu teléfono (opcional)"
                data-testid="contact-phone-input"
              />
              <select
                value={contactForm.service_interest}
                onChange={(e) => setContactForm({...contactForm, service_interest: e.target.value})}
                className="w-full px-4 py-3 rounded-lg border border-beige-300 focus:border-olive-500 transition-colors bg-white"
                data-testid="contact-service-select"
              >
                <option value="">Servicio de interés (opcional)</option>
                {services.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
              <textarea
                required
                value={contactForm.message}
                onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                className="w-full px-4 py-3 rounded-lg border border-beige-300 focus:border-olive-500 transition-colors bg-white h-32 resize-none"
                placeholder="Tu mensaje *"
                data-testid="contact-message-input"
              />
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-olive-500 hover:bg-olive-600 disabled:bg-olive-300 text-white py-4 rounded-full font-medium text-lg transition-all duration-300 flex items-center justify-center gap-2"
                data-testid="contact-submit-btn"
              >
                {isLoading ? (
                  <><Loader2 className="animate-spin" size={20} /> Enviando...</>
                ) : (
                  'Enviar Mensaje'
                )}
              </button>
            </motion.form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-foreground text-white py-12 px-6" data-testid="footer">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div>
              <h3 className="font-heading text-2xl mb-2">Decorando Vamos</h3>
              <p className="text-white/60">con Pamela E.</p>
            </div>
            <div className="flex items-center gap-6">
              <a
                href="https://instagram.com/decorandovamos"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-olive-400 transition-colors"
                data-testid="footer-instagram"
              >
                <Instagram size={24} />
              </a>
              <a
                href="https://wa.me/50767801300"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-olive-400 transition-colors"
                data-testid="footer-whatsapp"
              >
                <MessageCircle size={24} />
              </a>
            </div>
          </div>
          <div className="border-t border-white/10 mt-8 pt-8 text-center text-white/40 text-sm">
            <p>© {new Date().getFullYear()} Decorando Vamos con Pamela E. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>

      {/* Payment Modal */}
      {showPaymentModal && selectedService && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="payment-modal">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-8 max-w-md w-full max-h-[90vh] overflow-y-auto"
          >
            <h3 className="font-heading text-2xl mb-2">{selectedService.name}</h3>
            <p className="text-muted-foreground mb-4">{selectedService.description}</p>
            <p className="text-3xl font-bold text-olive-500 mb-6">${selectedService.price}</p>
            
            <form onSubmit={handlePayment} className="space-y-4">
              <input
                type="text"
                required
                value={paymentForm.customer_name}
                onChange={(e) => setPaymentForm({...paymentForm, customer_name: e.target.value})}
                className="w-full px-4 py-3 rounded-lg border border-beige-300 focus:border-olive-500 transition-colors"
                placeholder="Tu nombre completo *"
                data-testid="payment-name-input"
              />
              <input
                type="email"
                required
                value={paymentForm.customer_email}
                onChange={(e) => setPaymentForm({...paymentForm, customer_email: e.target.value})}
                className="w-full px-4 py-3 rounded-lg border border-beige-300 focus:border-olive-500 transition-colors"
                placeholder="Tu email *"
                data-testid="payment-email-input"
              />
              <input
                type="tel"
                value={paymentForm.customer_phone}
                onChange={(e) => setPaymentForm({...paymentForm, customer_phone: e.target.value})}
                className="w-full px-4 py-3 rounded-lg border border-beige-300 focus:border-olive-500 transition-colors"
                placeholder="WhatsApp (opcional)"
                data-testid="payment-phone-input"
              />
              
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowPaymentModal(false)}
                  className="flex-1 border-2 border-beige-300 text-foreground py-3 rounded-full font-medium hover:bg-beige-100 transition-colors"
                  data-testid="payment-cancel-btn"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 bg-olive-500 hover:bg-olive-600 disabled:bg-olive-300 text-white py-3 rounded-full font-medium transition-all flex items-center justify-center gap-2"
                  data-testid="payment-submit-btn"
                >
                  {isLoading ? (
                    <Loader2 className="animate-spin" size={20} />
                  ) : (
                    <>Pagar <ExternalLink size={16} /></>
                  )}
                </button>
              </div>
            </form>
            
            <p className="text-xs text-muted-foreground text-center mt-4">
              Serás redirigido a Stripe para completar el pago de forma segura
            </p>
          </motion.div>
        </div>
      )}

      {/* Portfolio Detail Modal */}
      {selectedPortfolioItem && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedPortfolioItem(null)}
          data-testid="portfolio-modal"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {selectedPortfolioItem.image_before ? (
              <div className="grid md:grid-cols-2">
                <div className="relative">
                  <span className="absolute top-4 left-4 bg-red-500 text-white text-sm px-3 py-1 rounded-full font-medium">
                    ANTES
                  </span>
                  <img 
                    src={selectedPortfolioItem.image_before}
                    alt="Antes"
                    className="w-full h-64 md:h-80 object-cover"
                  />
                </div>
                <div className="relative">
                  <span className="absolute top-4 left-4 bg-olive-500 text-white text-sm px-3 py-1 rounded-full font-medium">
                    DESPUÉS
                  </span>
                  <img 
                    src={selectedPortfolioItem.image_after}
                    alt="Después"
                    className="w-full h-64 md:h-80 object-cover"
                  />
                </div>
              </div>
            ) : (
              <img 
                src={selectedPortfolioItem.image_after}
                alt={selectedPortfolioItem.title}
                className="w-full h-64 md:h-96 object-cover"
              />
            )}
            <div className="p-6">
              <span className="text-olive-500 text-sm font-medium uppercase tracking-wider capitalize">{selectedPortfolioItem.category}</span>
              <h3 className="font-heading text-2xl font-medium mt-1 mb-3">{selectedPortfolioItem.title}</h3>
              {selectedPortfolioItem.description && (
                <p className="text-muted-foreground mb-4">{selectedPortfolioItem.description}</p>
              )}
              {selectedPortfolioItem.gallery && selectedPortfolioItem.gallery.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4" data-testid="portfolio-modal-gallery">
                  {selectedPortfolioItem.gallery.map((g, i) => (
                    <img key={i} src={g} alt={`${selectedPortfolioItem.title} ${i + 1}`} className="w-full h-28 object-cover rounded-lg" />
                  ))}
                </div>
              )}
              <div className="flex gap-3">
                <button
                  onClick={() => setSelectedPortfolioItem(null)}
                  className="flex-1 border-2 border-beige-300 text-foreground py-3 rounded-full font-medium hover:bg-beige-100 transition-colors"
                >
                  Cerrar
                </button>
                <a
                  href="https://wa.me/50767801300?text=Hola%20Pamela,%20vi%20tu%20proyecto%20y%20me%20gustaría%20una%20transformación%20similar"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 bg-olive-500 hover:bg-olive-600 text-white py-3 rounded-full font-medium transition-all text-center"
                >
                  ¡Lo quiero!
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
